"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  getSafeCategoryColor,
  getCategoryTextColor,
} from "@/utils/contrastUtils";
import { usePathname } from "next/navigation";
import { BlogCategory, CategoryPageProps, BlogPost } from "@/types/blog";
import { getAllCategories } from "@/utils/blogUtils";

const colorMap: Record<number, string> = {
  1: "#E0F2FE",
  2: "#FEF3C7",
  3: "#E2F7E1",
  4: "#FCE7F3",
  5: "#F3E8FF",
  6: "#FFEDD5",
  7: "#DBEAFE",
  8: "#DCFCE7",
  9: "#FAE8FF",
  10: "#FFE4E6",
};

const CategoryPage: React.FC<CategoryPageProps> = ({ catData }) => {
  const pathname = usePathname();
  const [category, setCategory] = useState<BlogCategory[]>([]);

  const fetchCategory = async () => {
    const respData = await getAllCategories();
    setCategory(respData);
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  return (
    <div className="container mx-auto bg-white px-8 pb-32 pt-20 lg:pt-24">
      <div className="flex items-center">
        <nav className="text-base">
          <ol className="mb-4 list-none p-0 text-black">
            <li className="inline-block text-sm font-medium">
              <Link href="/blog" className="text-gray-600 hover:text-gray-900">
                Home
              </Link>
            </li>
            <li className="mx-2 inline-block text-[#6a707c]">/</li>
            <li className="inline-block text-black">
              <span className="font-medium">{catData?.title}</span>
            </li>
          </ol>
        </nav>
      </div>
      <div className="flex flex-col items-center justify-between lg:flex-row">
        <div className="mb-8">
          <h1 className="mb-4 text-[32px] font-bold text-gray-900 lg:text-[40px]">
            {catData?.title}
          </h1>
          {catData?.description && (
            <p className="max-w-2xl text-lg text-gray-600">
              {catData.description}
            </p>
          )}
        </div>
        <div className="mb-8 flex flex-wrap gap-3">
          {category.map((item) => (
            <Link
              key={item?.title}
              href={`/blog/${item.slug}`}
              className={`min-w-[100px] rounded-[4px] border px-4 py-2 text-center text-sm transition-colors duration-200 ${
                pathname.includes(item.slug)
                  ? "border-[#6049E7] bg-[#6049E7]/5 text-[#6049E7]"
                  : "border-[#717171] bg-white text-[#202020] hover:border-[#6049E7] hover:text-[#6049E7]"
              }`}
            >
              {item?.title}
            </Link>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
        {catData.subcategory_detail?.map((item, index) => (
          <div
            key={`sub-cat-${index + 1}`}
            id={`sub-cat-${index + 1}`}
            className="group relative overflow-hidden rounded-2xl bg-white transition-all duration-300"
          >
            <Link className="block" href={`/blog/${item?.slug}`}>
              <div className="relative overflow-hidden">
                <Image
                  src={item?.image_url || ""}
                  alt={item?.title || "subcategory-image"}
                  className="h-48 w-full object-cover transition-transform duration-300 group-hover:scale-105 lg:h-56 xl:h-64 2xl:h-72"
                  width={400}
                  height={224}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <div className="absolute bottom-4 right-4">
                  <span
                    className="rounded-full px-4 py-2 text-sm font-bold text-[#202020] shadow-lg"
                    style={{
                      backgroundColor: getSafeCategoryColor(
                        colorMap[(index % 10) + 1],
                      ),
                    }}
                  >
                    {item?.post_count || 0} Articles
                  </span>
                </div>
              </div>
            </Link>
            <div className="rounded-b-2xl border border-gray-200 p-4 lg:p-6">
              <Link href={`/blog/${item?.slug}`}>
                <h3 className="mb-4 text-[20px] font-bold text-gray-900 transition-colors group-hover:text-[#6049E7] lg:text-[22px]">
                  {item?.title}
                </h3>
              </Link>
              {item?.description && (
                <p className="mb-6 line-clamp-3 leading-relaxed text-gray-600">
                  {item.description}
                </p>
              )}
              <Link href={`/blog/${item?.slug}`}>
                <div className="inline-flex items-center font-semibold text-[#6049E7] transition-colors hover:text-[#553ED0]">
                  Explore {item?.title} Articles
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                    />
                  </svg>
                </div>
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryPage;
